import { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Button, TextField, FormControl,
  InputLabel, Select, MenuItem, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Checkbox, Alert,
  CircularProgress, InputAdornment, Chip, LinearProgress,
} from '@mui/material';
import { ArrowBack, Save, Payment, AccountBalance, Receipt } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '';
const cfg = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const fmt = (v) => `$${parseFloat(v || 0).toFixed(2)}`;

export default function RegistrarPago() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const proveedorIdParam = searchParams.get('proveedorId');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [proveedores, setProveedores] = useState([]);
  const [comprasPendientes, setComprasPendientes] = useState([]);
  const [comprasSeleccionadas, setComprasSeleccionadas] = useState([]);
  const [cuentasContables, setCuentasContables] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [montosAplicar, setMontosAplicar] = useState({});

  const [form, setForm] = useState({
    proveedor_id: proveedorIdParam || '',
    fecha: new Date().toISOString().split('T')[0],
    forma_pago: 'EFECTIVO',
    referencia: '',
    banco_id: '',
    banco_nombre: '',
    numero_cheque: '',
    fecha_cheque: '',
    observaciones: '',
    cuenta_contable: '',
    cuenta_contable_nombre: '',
  });

  useEffect(() => {
    cargarProveedores();
    cargarCuentasContables();
    cargarBancos();
  }, []);

  useEffect(() => {
    if (form.proveedor_id) cargarComprasPendientes(form.proveedor_id);
    else { setComprasPendientes([]); setComprasSeleccionadas([]); setMontosAplicar({}); }
  }, [form.proveedor_id]);

  useEffect(() => {
    if (!cuentasContables.length) return;
    if (form.forma_pago === 'EFECTIVO') {
      const c = cuentasContables.find(c => c.codigo === '1.01.01.01' || c.nombre?.toLowerCase().includes('caja'));
      if (c) setForm(p => ({ ...p, cuenta_contable: c.codigo, cuenta_contable_nombre: `${c.codigo} - ${c.nombre}`, banco_id: '', banco_nombre: '' }));
    } else {
      const c = cuentasContables.find(c => c.codigo === '1.01.01.02' || c.nombre?.toLowerCase().includes('banco'));
      if (c) setForm(p => ({ ...p, cuenta_contable: c.codigo, cuenta_contable_nombre: `${c.codigo} - ${c.nombre}` }));
    }
  }, [form.forma_pago, cuentasContables]);

  const cargarCuentasContables = async () => {
    try {
      const res = await axios.get(`${API}/api/contabilidad/plan-cuentas`, cfg());
      const all = res.data?.data || res.data || [];
      const mov = all.filter(c => c.acepta_movimiento && (c.codigo?.startsWith('1.01.01') || c.nombre?.toLowerCase().includes('caja') || c.nombre?.toLowerCase().includes('banco')));
      setCuentasContables(mov.length ? mov : all.filter(c => c.acepta_movimiento));
    } catch (_) {}
  };

  const cargarBancos = async () => {
    try { const r = await axios.get(`${API}/api/tesoreria/bancos`, cfg()); setBancos(r.data?.data || r.data || []); } catch (_) {}
  };

  const cargarProveedores = async () => {
    try { const r = await axios.get(`${API}/api/cuentas-pagar/proveedores-con-saldo`, cfg()); setProveedores(r.data.data || []); } catch (_) {}
  };

  const cargarComprasPendientes = async (pid) => {
    try {
      const r = await axios.get(`${API}/api/cuentas-pagar/compras-pendientes?proveedorId=${pid}`, cfg());
      const data = r.data.data || [];
      setComprasPendientes(data);
      // Auto-seleccionar todas las compras y pre-llenar montos
      setComprasSeleccionadas(data);
      const montos = {};
      data.forEach(c => {
        const saldo = c.saldo_pendiente !== undefined ? parseFloat(c.saldo_pendiente) : parseFloat(c.totales?.importeTotal || c.total || 0) - parseFloat(c.total_pagado || 0);
        montos[c.id] = Math.max(0, saldo).toFixed(2);
      });
      setMontosAplicar(montos);
    } catch (_) { setError('Error al cargar compras'); }
  };

  const getSaldo = (c) => c.saldo_pendiente !== undefined ? parseFloat(c.saldo_pendiente) : parseFloat(c.totales?.importeTotal || c.total || 0) - parseFloat(c.total_pagado || 0);
  const getTotal = (c) => parseFloat(c.totales?.importeTotal || c.total || 0);

  const toggleCompra = (c) => {
    const sel = comprasSeleccionadas.some(x => x.id === c.id);
    if (sel) {
      setComprasSeleccionadas(comprasSeleccionadas.filter(x => x.id !== c.id));
      const m = { ...montosAplicar }; delete m[c.id]; setMontosAplicar(m);
    } else {
      setComprasSeleccionadas([...comprasSeleccionadas, c]);
      setMontosAplicar({ ...montosAplicar, [c.id]: getSaldo(c).toFixed(2) });
    }
  };

  const totalPago = Object.values(montosAplicar).reduce((s, m) => s + parseFloat(m || 0), 0);
  const mostrarBancos = ['CHEQUE','TRANSFERENCIA','TARJETA_CREDITO'].includes(form.forma_pago);

  const handleSubmit = async () => {
    if (!comprasSeleccionadas.length) return setError('Seleccione al menos una compra');
    if (!form.cuenta_contable) return setError('Seleccione una cuenta contable');
    // Validar banco obligatorio cuando la forma de pago no es efectivo
    if (mostrarBancos && !form.banco_id) {
      if (bancos.length === 0) return setError('No tiene cuentas bancarias configuradas. Configure una en Tesorería → Bancos antes de registrar este pago.');
      return setError('Debe seleccionar el banco o cuenta bancaria desde donde se realiza el pago');
    }
    for (const c of comprasSeleccionadas) {
      const m = parseFloat(montosAplicar[c.id] || 0);
      if (m <= 0) return setError('El monto debe ser mayor a 0');
      if (m > getSaldo(c) + 0.01) return setError('El monto excede el saldo pendiente');
    }
    try {
      setLoading(true); setError(null);
      const prov = proveedores.find(p => p.proveedor_id === form.proveedor_id);
      const payload = { ...form, proveedor_nombre: prov?.proveedor_nombre || '' };
      let res;
      if (comprasSeleccionadas.length === 1) {
        const c = comprasSeleccionadas[0];
        res = await axios.post(`${API}/api/cuentas-pagar/pagos`, { ...payload, compra_id: c.id, monto: parseFloat(montosAplicar[c.id]) }, cfg());
      } else {
        res = await axios.post(`${API}/api/cuentas-pagar/pagos/multiple`, { ...payload, compras: comprasSeleccionadas.map(c => ({ compra_id: c.id, monto: parseFloat(montosAplicar[c.id]) })) }, cfg());
      }
      const pagoId = Array.isArray(res.data?.data) ? res.data.data[0]?.id : res.data?.data?.id;
      setSuccess('Pago registrado y asiento contable generado exitosamente.');
      if (pagoId) {
        try {
          const pdf = await axios.get(`${API}/api/cuentas-pagar/pagos/${pagoId}/pdf`, { ...cfg(), responseType: 'blob' });
          const url = URL.createObjectURL(new Blob([pdf.data]));
          const a = document.createElement('a'); a.href = url;
          a.download = `Comprobante_Pago_${form.referencia || pagoId.slice(0,8)}.pdf`;
          document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
        } catch (_) {}
      }
      setTimeout(() => navigate('/cuentas-pagar'), 2500);
    } catch (e) { setError(e.response?.data?.message || 'Error al registrar pago'); }
    finally { setLoading(false); }
  };

  const provSeleccionado = proveedores.find(p => p.proveedor_id === form.proveedor_id);

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 }, width: '100%', boxSizing: 'border-box' }}>

      {/* HEADER */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/cuentas-pagar')} variant="outlined" size="small">Volver</Button>
        <Box sx={{ width: 36, height: 36, borderRadius: 1.5, bgcolor: '#fff3e0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Payment sx={{ color: '#e65100' }} />
        </Box>
        <Box>
          <Typography variant="h5" fontWeight={700}>Registrar Pago a Proveedor</Typography>
          <Typography variant="body2" color="text.secondary">Aplica el pago y genera el asiento contable automáticamente</Typography>
        </Box>
      </Box>

      {error   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 16, alignItems: 'start' }}>

        {/* ── COLUMNA IZQUIERDA ── */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* Bloque proveedor + fecha + forma pago */}
          <Paper sx={{ p: 2.5, borderRadius: 2 }}>
            <Typography variant="body2" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 0.75 }}>
              🏭 Datos del Proveedor y Pago
            </Typography>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px 160px', gap: 12, marginBottom: 12 }}>
              <FormControl fullWidth required size="small">
                <InputLabel>Proveedor</InputLabel>
                <Select value={form.proveedor_id} label="Proveedor"
                  onChange={e => { setForm({ ...form, proveedor_id: e.target.value }); setComprasSeleccionadas([]); setMontosAplicar({}); }}>
                  {proveedores.map(p => (
                    <MenuItem key={p.proveedor_id} value={p.proveedor_id}>
                      {p.proveedor_nombre} — <span style={{ color: '#d32f2f', fontWeight: 700 }}>{fmt(p.saldo_total)}</span>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField size="small" required type="date" label="Fecha" value={form.fecha}
                onChange={e => setForm({ ...form, fecha: e.target.value })} InputLabelProps={{ shrink: true }} />
              <FormControl fullWidth required size="small">
                <InputLabel>Forma de Pago</InputLabel>
                <Select value={form.forma_pago} label="Forma de Pago"
                  onChange={e => setForm({ ...form, forma_pago: e.target.value })}>
                  <MenuItem value="EFECTIVO">💵 Efectivo</MenuItem>
                  <MenuItem value="TRANSFERENCIA">🏦 Transferencia</MenuItem>
                  <MenuItem value="CHEQUE">📄 Cheque</MenuItem>
                  <MenuItem value="TARJETA_CREDITO">💳 Tarjeta</MenuItem>
                </Select>
              </FormControl>
            </div>

            {/* Cuenta contable + banco */}
            <div style={{ display: 'grid', gridTemplateColumns: mostrarBancos ? '1fr 1fr' : '1fr', gap: 12, marginBottom: form.forma_pago === 'CHEQUE' ? 12 : 0 }}>
              <FormControl fullWidth required size="small">
                <InputLabel>Cuenta Contable (Origen del dinero)</InputLabel>
                <Select value={form.cuenta_contable} label="Cuenta Contable (Origen del dinero)"
                  onChange={e => {
                    const c = cuentasContables.find(x => x.codigo === e.target.value);
                    setForm({ ...form, cuenta_contable: e.target.value, cuenta_contable_nombre: c ? `${c.codigo} - ${c.nombre}` : '' });
                  }}>
                  {cuentasContables.map(c => <MenuItem key={c.id || c.codigo} value={c.codigo}>{c.codigo} - {c.nombre}</MenuItem>)}
                </Select>
              </FormControl>
              {mostrarBancos && (
                <FormControl fullWidth size="small" error={mostrarBancos && !form.banco_id}>
                  <InputLabel>Banco *</InputLabel>
                  <Select value={form.banco_id} label="Banco *"
                    onChange={e => { const b = bancos.find(x => x.id === e.target.value); setForm({ ...form, banco_id: e.target.value, banco_nombre: b?.nombre_banco || b?.nombre || '' }); }}>
                    <MenuItem value="">— Seleccione banco —</MenuItem>
                    {bancos.map(b => <MenuItem key={b.id} value={b.id}>{b.nombre_banco || b.nombre}{b.numero_cuenta ? ` (****${String(b.numero_cuenta).slice(-4)})` : ''}</MenuItem>)}
                  </Select>
                  {mostrarBancos && !form.banco_id && (
                    bancos.length === 0
                      ? <Box sx={{ mt: 1 }}>
                          <Alert severity="warning" sx={{ fontSize: 11, py: 0.5 }}
                            action={<Button size="small" onClick={() => navigate('/tesoreria/bancos')} sx={{ fontSize: 10, textTransform: 'none' }}>Configurar →</Button>}>
                            Sin cuentas bancarias. Configure una en Tesorería primero.
                          </Alert>
                        </Box>
                      : <Typography sx={{ fontSize: 11, color: 'error.main', mt: 0.5, ml: 1.5 }}>
                          Seleccione el banco desde donde sale el dinero
                        </Typography>
                  )}
                </FormControl>
              )}
            </div>
            {form.forma_pago === 'CHEQUE' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <TextField size="small" label="No. Cheque" value={form.numero_cheque} onChange={e => setForm({ ...form, numero_cheque: e.target.value })} />
                <TextField size="small" type="date" label="Fecha Cheque" value={form.fecha_cheque} onChange={e => setForm({ ...form, fecha_cheque: e.target.value })} InputLabelProps={{ shrink: true }} />
              </div>
            )}
            <Box sx={{ mt: 1.5 }}>
              <TextField fullWidth size="small" label="Referencia / No. Comprobante" value={form.referencia}
                onChange={e => setForm({ ...form, referencia: e.target.value })} sx={{ mb: 1.5 }} />
              <TextField fullWidth size="small" multiline rows={2} label="Observaciones"
                value={form.observaciones} onChange={e => setForm({ ...form, observaciones: e.target.value })} />
            </Box>
          </Paper>

          {/* Lista de compras pendientes */}
          {form.proveedor_id && (
            <Paper sx={{ borderRadius: 2 }}>
              <Box sx={{ px: 2, py: 1.5, bgcolor: '#e65100', color: '#fff', borderRadius: '8px 8px 0 0', display: 'flex', alignItems: 'center', gap: 1 }}>
                <Receipt sx={{ fontSize: 18 }} />
                <Typography fontWeight={700} fontSize={14}>Compras Pendientes de Pago</Typography>
                <Chip label={comprasPendientes.length} size="small" sx={{ bgcolor: '#fff', color: '#e65100', fontWeight: 700, ml: 1 }} />
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f7fa', '& th': { fontSize: 11, fontWeight: 700, py: 0.75 } }}>
                      <TableCell padding="checkbox" />
                      <TableCell>Fecha</TableCell>
                      <TableCell>No. Factura</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell sx={{ minWidth: 100 }}>Pagado</TableCell>
                      <TableCell align="right">Saldo</TableCell>
                      <TableCell align="right">Monto a Pagar</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {comprasPendientes.length === 0 ? (
                      <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary" variant="body2">No hay compras pendientes para este proveedor</Typography>
                      </TableCell></TableRow>
                    ) : comprasPendientes.map((c, i) => {
                      const saldo = getSaldo(c);
                      const total = getTotal(c);
                      const pagado = total - saldo;
                      const sel = comprasSeleccionadas.some(x => x.id === c.id);
                      const progreso = total > 0 ? Math.round((pagado / total) * 100) : 0;
                      return (
                        <TableRow key={c.id} hover selected={sel}
                          sx={{ bgcolor: sel ? '#fff3e0' : i % 2 === 0 ? '#fff' : '#fafafa', cursor: 'pointer', '& td': { fontSize: 12, py: 0.8 } }}
                          onClick={() => toggleCompra(c)}>
                          <TableCell padding="checkbox"><Checkbox checked={sel} size="small" onChange={() => {}} /></TableCell>
                          <TableCell>{c.fecha_emision || new Date(c.created_at).toLocaleDateString('es-EC')}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{c.factura?.numeroComprobante || '—'}</TableCell>
                          <TableCell align="right">{fmt(total)}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <LinearProgress variant="determinate" value={progreso}
                                sx={{ flex: 1, height: 5, borderRadius: 3, bgcolor: '#e0e0e0', '& .MuiLinearProgress-bar': { bgcolor: progreso > 0 ? '#4caf50' : '#e0e0e0' } }} />
                              <Typography sx={{ fontSize: 10, minWidth: 28 }}>{progreso}%</Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, color: '#d32f2f' }}>{fmt(saldo)}</TableCell>
                          <TableCell align="right" onClick={e => e.stopPropagation()}>
                            {sel && (
                              <TextField type="number" size="small" value={montosAplicar[c.id] || ''}
                                onChange={e => setMontosAplicar({ ...montosAplicar, [c.id]: e.target.value })}
                                InputProps={{ startAdornment: <InputAdornment position="start" sx={{ '& p': { fontSize: 12 } }}>$</InputAdornment> }}
                                inputProps={{ min: 0, max: saldo, step: 0.01 }} sx={{ width: 110 }} />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </Box>

        {/* ── COLUMNA DERECHA: RESUMEN + ACCIÓN ── */}
        <Box sx={{ position: 'sticky', top: 80, display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* Resumen proveedor */}
          {provSeleccionado && (
            <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid #ffe0b2' }}>
              <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: .5 }}>Proveedor seleccionado</Typography>
              <Typography variant="body1" fontWeight={700} sx={{ mt: 0.5 }}>{provSeleccionado.proveedor_nombre}</Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                <Typography variant="body2" color="text.secondary">Deuda pendiente</Typography>
                <Typography variant="body2" fontWeight={700} color="error.main">{fmt(provSeleccionado.saldo_total)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Facturas</Typography>
                <Typography variant="body2">{provSeleccionado.compras_pendientes}</Typography>
              </Box>
            </Paper>
          )}

          {/* Resumen del pago */}
          <Paper sx={{ p: 2.5, borderRadius: 2, border: '2px solid #e65100' }}>
            <Typography variant="body2" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <AccountBalance fontSize="small" sx={{ color: '#e65100' }} /> Resumen del Pago
            </Typography>

            {comprasSeleccionadas.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography sx={{ fontSize: 32 }}>☑️</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Selecciona compras de la lista</Typography>
              </Box>
            ) : (
              <>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid #f0f0f0' }}>
                    <Typography variant="body2" color="text.secondary">Facturas seleccionadas</Typography>
                    <Chip label={comprasSeleccionadas.length} size="small" color="warning" />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid #f0f0f0' }}>
                    <Typography variant="body2" color="text.secondary">Forma de pago</Typography>
                    <Typography variant="body2" fontWeight={600}>{form.forma_pago}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid #f0f0f0' }}>
                    <Typography variant="body2" color="text.secondary">Cuenta contable</Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ fontSize: 11 }}>{form.cuenta_contable}</Typography>
                  </Box>
                </Box>

                <Box sx={{ p: 1.5, bgcolor: '#fff3e0', borderRadius: 1.5, mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">TOTAL A PAGAR</Typography>
                  <Typography variant="h4" fontWeight={700} color="#e65100" sx={{ lineHeight: 1.2 }}>{fmt(totalPago)}</Typography>
                </Box>

                <Alert severity="info" sx={{ fontSize: 11, py: 0.5 }}>
                  Se generará asiento contable automático al registrar el pago.
                </Alert>
              </>
            )}

            <Button fullWidth variant="contained" size="large" onClick={handleSubmit}
              disabled={loading || comprasSeleccionadas.length === 0}
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <Save />}
              sx={{ mt: 2, bgcolor: '#e65100', '&:hover': { bgcolor: '#bf360c' }, fontWeight: 700, py: 1.25 }}>
              {loading ? 'Registrando...' : 'Registrar Pago'}
            </Button>
            <Button fullWidth variant="outlined" onClick={() => navigate('/cuentas-pagar')}
              disabled={loading} sx={{ mt: 1 }}>
              Cancelar
            </Button>
          </Paper>
        </Box>
      </div>
    </Box>
  );
}
